import {
  DealResponseDto,
  FieldDefinition,
  MarketplaceCardResponseDto,
  ProfileId,
  StepDefinition,
} from "../../types/types";
import type { TranslationFn } from "../../pages/deals/createDeal/dealFormFields";
import { dateYearHasFourDigits } from "../common/dateInput";
import { getDateFieldRuleError } from "./dateFieldRules";
import { getUniqueAcrossItemsError } from "../common/uniqueAcrossItems";
import {
  getOutcomePolicyFormValue,
  getOutcomeRegistryRows,
} from "../opportunities/outcomeRegistryHelpers";

export type DealTemplateStep = StepDefinition & {
  required?: boolean;
  validation?: FieldDefinition["validation"];
  outcomeRegistry?: unknown;
};

export const isComponentValid = (component: unknown): boolean => {
  if (!component) return false;
  const value = component as Record<string, unknown>;
  if ("componentType" in value && !value.componentType) return false;
  if ("kind" in value && !value.kind) return false;
  if ("mode" in value && !value.mode) return false;
  if (
    "value" in value &&
    (value.value === undefined ||
      value.value === null ||
      isNaN(Number(value.value)))
  ) {
    return false;
  }
  if ("fundingStage" in value && !value.fundingStage) return false;
  if ("triggerType" in value && !value.triggerType) return false;
  if (String(value.triggerType) === "outcome" && !value.outcome) {
    return false;
  }
  if (
    String(value.triggerType) === "flag" &&
    (!value.flag || String(value.flag).trim() === "")
  ) {
    return false;
  }
  if ("paymentTiming" in value && !value.paymentTiming) return false;
  return true;
};

const getStepId = (stepConfig: DealTemplateStep): string =>
  stepConfig.stepId ||
  String(stepConfig.title || "step")
    .toLowerCase()
    .replace(/\s+/g, "_");

const getByPath = (source: unknown, path: string): unknown => {
  if (!source || !path) return undefined;
  if (Object.prototype.hasOwnProperty.call(source, path)) {
    return (source as Record<string, unknown>)[path];
  }
  return path
    .split(".")
    .reduce(
      (accumulator: unknown, key) =>
        accumulator != null
          ? (accumulator as Record<string, unknown>)[key]
          : undefined,
      source,
    );
};

const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "");

const resolveConditionValue = (
  formData: unknown,
  stepId: string,
  key: string,
  context?: Record<string, unknown>,
): unknown => {
  const contextValue = getByPath(context, key);
  if (contextValue !== undefined) return contextValue;

  const formDataRecord = formData as Record<string, unknown> | undefined;
  const stepValue = getByPath(formDataRecord?.[stepId], key);
  if (stepValue !== undefined) return stepValue;

  for (const value of Object.values(
    (formData || {}) as Record<string, unknown>,
  )) {
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    const resolved = getByPath(value, key);
    if (resolved !== undefined) return resolved;
  }

  return undefined;
};

const matchesConditions = (
  conditions: Record<string, unknown> | undefined,
  formData: unknown,
  stepId: string,
  context?: Record<string, unknown>,
): boolean => {
  if (!conditions) return true;

  return Object.entries(conditions).every(([key, expected]) => {
    const actual = resolveConditionValue(formData, stepId, key, context);
    return Array.isArray(expected)
      ? expected.includes(actual)
      : actual === expected;
  });
};

const isFieldVisibleForValidation = (
  fieldDef: FieldDefinition,
  formData: unknown,
  stepId: string,
  context?: Record<string, unknown>,
): boolean => matchesConditions(fieldDef.visibleWhen, formData, stepId, context);

const isFieldRequiredForValidation = (
  fieldDef: FieldDefinition,
  formData: unknown,
  stepId: string,
  context?: Record<string, unknown>,
): boolean => {
  if (fieldDef.validation?.required) return true;
  if (!fieldDef.validation?.requiredWhen) return false;
  return matchesConditions(
    fieldDef.validation.requiredWhen,
    formData,
    stepId,
    context,
  );
};

const getFieldLabel = (
  fieldDef: FieldDefinition,
  t: TranslationFn,
): string => {
  const label = fieldDef.label || fieldDef.key;
  return t(label);
};

const getRequiredFieldMessage = (
  fieldDef: FieldDefinition,
  t: TranslationFn,
): string => {
  const label = getFieldLabel(fieldDef, t);
  return t("requiredField", { field: label });
};

const formatComponentLabelForUi = (label: unknown): string => {
  const rawText = String(label ?? "");
  const hasUnderscore = rawText.includes("_");
  const text = rawText
    .replace(/_+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return "";

  const shouldFormat =
    hasUnderscore ||
    text.split(" ").some((word) => /^[A-Z0-9]{4,}$/.test(word));

  if (!shouldFormat) return text;

  return text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getDateYearErrorMessage = (
  label: string,
  t: TranslationFn,
): string => {
  const key = "dateYearMustBeFourDigits";
  const message = t(key, { field: label });
  return message && message !== key
    ? message
    : `${label} year must be 4 digits`;
};


const addComponentValues = (target: Set<string>, value: unknown) => {
  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (!isEmptyValue(item)) target.add(String(item));
    });
    return;
  }

  if (!isEmptyValue(value)) {
    target.add(String(value));
  }
};

const getComponentsFromOptionsSource = (
  formData: unknown,
  optionsFrom?: string,
): string[] => {
  const source = String(optionsFrom || "").trim();
  if (!source) return [];

  const dotIndex = source.indexOf(".");
  if (dotIndex === -1) return [];

  const sourceStepId = source.slice(0, dotIndex);
  const sourceFieldPath = source.slice(dotIndex + 1);
  const sourceStepData = (formData as Record<string, unknown> | undefined)?.[
    sourceStepId
  ];
  const components = new Set<string>();

  if (Array.isArray(sourceStepData)) {
    sourceStepData.forEach((item) => {
      addComponentValues(components, getByPath(item, sourceFieldPath));
    });
  } else {
    addComponentValues(
      components,
      getByPath(sourceStepData, sourceFieldPath),
    );
  }

  return Array.from(components);
};

const getUnassignedPayoutComponents = (
  stepConfig: DealTemplateStep,
  formData: unknown,
): string[] => {
  if (!stepConfig?.outcomeRegistry) return [];

  const stepId = getStepId(stepConfig);
  const rows = getOutcomeRegistryRows(stepConfig.outcomeRegistry);
  const availableComponents = new Set<string>();
  const appliedComponents = new Set<string>();

  rows.forEach((row) => {
    getComponentsFromOptionsSource(formData, row.optionsFrom).forEach(
      (component) => availableComponents.add(component),
    );

    const policyValue = getOutcomePolicyFormValue(
      row,
      getByPath(
        (formData as Record<string, unknown> | undefined)?.[stepId],
        `outcomes.${row.key}`,
      ),
    );

    policyValue.selectedComponents.forEach((component) => {
      if (!isEmptyValue(component)) {
        appliedComponents.add(String(component));
      }
    });
  });

  return Array.from(availableComponents).filter(
    (component) => !appliedComponents.has(component),
  );
};

const getPayoutComponentsErrorMessage = (
  components: string[],
  t: TranslationFn,
): string => {
  const componentList = components.map(formatComponentLabelForUi).join(", ");
  return t("payoutPolicy.componentsMustBeApplied", {
    components: componentList,
  });
};

const addPayoutPolicyValidationError = (
  errors: Record<string, string>,
  stepConfig: DealTemplateStep,
  formData: unknown,
  t: TranslationFn,
): boolean => {
  const unassignedComponents = getUnassignedPayoutComponents(
    stepConfig,
    formData,
  );

  if (unassignedComponents.length === 0) return false;

  const stepId = getStepId(stepConfig);
  errors[`${stepId}.components`] = getPayoutComponentsErrorMessage(
    unassignedComponents,
    t,
  );

  return true;
};

const addFieldValidationErrors = (
  errors: Record<string, string>,
  fieldDef: FieldDefinition,
  value: unknown,
  errorKey: string,
  formData: unknown,
  stepId: string,
  t: TranslationFn,
  context?: Record<string, unknown>,
  repeatableItems?: unknown[],
  repeatableIndex?: number,
) => {
  if (!isFieldVisibleForValidation(fieldDef, formData, stepId, context)) return;

  const label = getFieldLabel(fieldDef, t);

  if (fieldDef.fields && Array.isArray(fieldDef.fields)) {
    const nestedObject =
      value && typeof value === "object" ? value : {};
    const nestedContext = { ...(context || {}), ...nestedObject };

    for (const nestedField of fieldDef.fields) {
      const nestedValue = getByPath(nestedObject, nestedField.key);
      addFieldValidationErrors(
        errors,
        nestedField,
        nestedValue,
        `${errorKey}.${nestedField.key}`,
        formData,
        stepId,
        t,
        nestedContext,
        repeatableItems,
        repeatableIndex,
      );
    }
    return;
  }

  if (isFieldRequiredForValidation(fieldDef, formData, stepId, context)) {
    if (isEmptyValue(value)) {
      errors[errorKey] = getRequiredFieldMessage(fieldDef, t);
      return;
    }
  }

  if (
    fieldDef.type === "date" &&
    !isEmptyValue(value) &&
    !dateYearHasFourDigits(value)
  ) {
    errors[errorKey] = getDateYearErrorMessage(label, t);
    return;
  }

  if (fieldDef.type === "date" && !isEmptyValue(value)) {
    const dateRuleError = getDateFieldRuleError(fieldDef, value, {
      formData,
      stepId,
      contextData: context,
    });
    if (dateRuleError) {
      errors[errorKey] = dateRuleError;
      return;
    }
  }

  const uniqueAcrossItemsError = getUniqueAcrossItemsError(
    fieldDef,
    value,
    repeatableItems,
    repeatableIndex,
  );
  if (uniqueAcrossItemsError) {
    errors[errorKey] = uniqueAcrossItemsError;
    return;
  }

  if (
    fieldDef.type === "number" &&
    fieldDef.validation?.min !== undefined &&
    !isEmptyValue(value)
  ) {
    const numericValue = Number(value);
    if (isNaN(numericValue) || numericValue < Number(fieldDef.validation.min)) {
      errors[errorKey] = t("minValueMustBe", { min: fieldDef.validation.min });
    }
  }

  if (fieldDef.validation?.maxLength && typeof value === "string") {
    if (value.length > Number(fieldDef.validation.maxLength)) {
      errors[errorKey] = t("tooLong");
    }
  }
};

export const validateStepData = (
  stepIndex: number,
  draftCard: MarketplaceCardResponseDto,
  getTemplateSteps: (card: MarketplaceCardResponseDto | null) => DealTemplateStep[],
  t: TranslationFn,
  chosenFighterId?: ProfileId | null,
  isGroupChat?: boolean
): { isValid: boolean; errors: Record<string, string> } => {
  const validationResult: { isValid: boolean; errors: Record<string, string> } = {
    isValid: true,
    errors: {},
  };
  if (!draftCard) return validationResult;

  const templateSteps = getTemplateSteps(draftCard);
  if (stepIndex >= templateSteps.length) return validationResult;

  const stepConfig = templateSteps[stepIndex];
  if (!stepConfig) return validationResult;

  const stepId = getStepId(stepConfig);
  const formData: Record<string, unknown> =
    draftCard.opportunityCardResponse?.formData || {};

  addPayoutPolicyValidationError(
    validationResult.errors,
    stepConfig,
    formData,
    t,
  );

  if (stepConfig.repeatable) {
    const componentsArray: unknown = formData[stepId] || [];
    const isRequired = Boolean(
      stepConfig.required || stepConfig.validation?.required,
    );
    if (!Array.isArray(componentsArray) || componentsArray.length === 0) {
      if (isRequired) {
        validationResult.errors[`${stepId}.components`] = t("componentsInvalid");
      }
    } else {
      const invalidComponents = componentsArray.filter(
        (component) => !isComponentValid(component),
      );
      if (invalidComponents.length > 0) {
        validationResult.errors[`${stepId}.components`] =
          t("componentsInvalid");
      }
      componentsArray.forEach((component, index) => {
        for (const fieldDef of stepConfig.fields || []) {
          const fieldValue = getByPath(component, fieldDef.key);
          addFieldValidationErrors(
            validationResult.errors,
            fieldDef,
            fieldValue,
            `${stepId}[${index}].${fieldDef.key}`,
            formData,
            stepId,
            t,
            { ...(component as Record<string, unknown>) },
            componentsArray,
            index,
          );
        }
      });
    }
    return {
      isValid: Object.keys(validationResult.errors).length === 0,
      errors: validationResult.errors,
    };
  }

  for (const fieldDef of stepConfig.fields || []) {
    const value = getByPath(formData[stepId], fieldDef.key);
    addFieldValidationErrors(
      validationResult.errors,
      fieldDef,
      value,
      `${stepId}.${fieldDef.key}`,
      formData,
      stepId,
      t,
      { ...((formData[stepId] || {}) as Record<string, unknown>) },
    );
  }

  return {
    isValid: Object.keys(validationResult.errors).length === 0,
    errors: validationResult.errors,
  };
};

export const validateWholeForm = (
  card: MarketplaceCardResponseDto,
  getTemplateSteps: (card: MarketplaceCardResponseDto | null) => DealTemplateStep[],
  t: TranslationFn,
  chosenFighterId?: ProfileId | null,
  isGroupChat?: boolean
): { isValid: boolean; newErrors: Record<string, string> } => {
  const templateSteps = getTemplateSteps(card);
  const formData: Record<string, unknown> =
    card.opportunityCardResponse?.formData || {};
  const newErrors: Record<string, string> = {};
  let isValid = true;

  for (const step of templateSteps) {
    const stepId = getStepId(step);
    const fields = step.fields || [];

    if (addPayoutPolicyValidationError(newErrors, step, formData, t)) {
      isValid = false;
    }

    if (step.repeatable) {
      const componentsArray: unknown = formData[stepId] || [];
      const isRequired = Boolean(
        step.required || step.validation?.required,
      );
      if (Array.isArray(componentsArray)) {
        if (componentsArray.length === 0 && isRequired) {
          newErrors[`${stepId}.components`] = t("componentsInvalid");
          isValid = false;
        }
        for (let index = 0; index < componentsArray.length; index++) {
          for (const fieldDef of fields) {
            const rowContext = componentsArray[index] || {};
            const fieldValue = getByPath(rowContext, fieldDef.key);
            if (
              fieldDef.validation?.required &&
              (fieldValue === undefined ||
                fieldValue === null ||
                (typeof fieldValue === "string" &&
                  fieldValue.trim() === ""))
            ) {
              newErrors[`${stepId}[${index}].${fieldDef.key}`] =
                getRequiredFieldMessage(fieldDef, t);
              isValid = false;
            }
            if (
              fieldDef.type === "date" &&
              !isEmptyValue(fieldValue) &&
              !dateYearHasFourDigits(fieldValue)
            ) {
              newErrors[`${stepId}[${index}].${fieldDef.key}`] =
                getDateYearErrorMessage(getFieldLabel(fieldDef, t), t);
              isValid = false;
            }
            const dateRuleError = getDateFieldRuleError(fieldDef, fieldValue, {
              formData,
              stepId,
              contextData: rowContext,
            });
            if (dateRuleError) {
              newErrors[`${stepId}[${index}].${fieldDef.key}`] = dateRuleError;
              isValid = false;
            }
            const uniqueAcrossItemsError = getUniqueAcrossItemsError(
              fieldDef,
              fieldValue,
              componentsArray,
              index,
            );
            if (uniqueAcrossItemsError) {
              newErrors[`${stepId}[${index}].${fieldDef.key}`] =
                uniqueAcrossItemsError;
              isValid = false;
            }
            if (
              fieldDef.type === "number" &&
              fieldDef.validation?.min !== undefined
            ) {
              const numericValue = Number(fieldValue);
              if (
                isNaN(numericValue) ||
                numericValue < Number(fieldDef.validation.min)
              ) {
                newErrors[`${stepId}[${index}].${fieldDef.key}`] =
                  t("minValueMustBe", { min: fieldDef.validation.min });
                isValid = false;
              }
            }
          }
        }
      } else {
        if (isRequired) {
          newErrors[`${stepId}.components`] = t("componentsInvalid");
          isValid = false;
        }
      }
      continue;
    }

    for (const fieldDef of fields) {
      const label = getFieldLabel(fieldDef, t);
      const context = {
        ...((formData[stepId] || {}) as Record<string, unknown>),
      };
      const value = getByPath(formData[stepId], fieldDef.key);

      if (!isFieldVisibleForValidation(fieldDef, formData, stepId, context)) {
        continue;
      }

      if (isFieldRequiredForValidation(fieldDef, formData, stepId, context)) {
        if (fieldDef.fields && Array.isArray(fieldDef.fields)) {
          const nestedObject = (value || {}) as Record<string, unknown>;
          for (const nestedField of fieldDef.fields) {
            if (
              nestedField.validation?.required &&
              (nestedObject[nestedField.key] === undefined || nestedObject[nestedField.key] === "")
            ) {
              newErrors[`${stepId}.${fieldDef.key}.${nestedField.key}`] =
                getRequiredFieldMessage(nestedField, t);
              isValid = false;
            }
          }
        } else {
          if (
            value === undefined ||
            value === null ||
            (typeof value === "string" && value.trim() === "")
          ) {
            newErrors[`${stepId}.${fieldDef.key}`] =
              getRequiredFieldMessage(fieldDef, t);
            isValid = false;
          }
        }
      }

      if (
        fieldDef.type === "number" &&
        fieldDef.validation?.min !== undefined &&
        !isEmptyValue(value)
      ) {
        const numericValue = Number(value);
        if (isNaN(numericValue) || numericValue < Number(fieldDef.validation.min)) {
          newErrors[`${stepId}.${fieldDef.key}`] =
            t("minValueMustBe", { min: fieldDef.validation.min });
          isValid = false;
        }
      }

      if (
        fieldDef.type === "date" &&
        !isEmptyValue(value) &&
        !dateYearHasFourDigits(value)
      ) {
        newErrors[`${stepId}.${fieldDef.key}`] =
          getDateYearErrorMessage(label, t);
        isValid = false;
      }

      const dateRuleError = getDateFieldRuleError(fieldDef, value, {
        formData,
        stepId,
        contextData: context,
      });
      if (dateRuleError) {
        newErrors[`${stepId}.${fieldDef.key}`] = dateRuleError;
        isValid = false;
      }

      if (fieldDef.validation?.maxLength && typeof value === "string") {
        if (value.length > Number(fieldDef.validation.maxLength)) {
          newErrors[`${stepId}.${fieldDef.key}`] = t("tooLong");
          isValid = false;
        }
      }

      if (fieldDef.type === "date" && fieldDef.validation?.required) {
        if (!value || String(value).trim() === "") {
          newErrors[`${stepId}.${fieldDef.key}`] =
            getRequiredFieldMessage(fieldDef, t);
          isValid = false;
        }
      }
    }
  }

  if (
    isGroupChat &&
    (chosenFighterId === null || chosenFighterId === undefined)
  ) {
    newErrors["selectedFighterId"] = t("requiredField", {
      field: t("selectFighter"),
    });
    isValid = false;
  }

  return { isValid, newErrors };
};

export const extractFormDataFromDeal = (
  deal: DealResponseDto,
): Record<string, unknown> => deal.formData ?? {};

export const extractCurrency = (deal: DealResponseDto): string => {
  const formData = extractFormDataFromDeal(deal);
  const core = formData.core as Record<string, unknown> | undefined;
  return (core?.currency as string) ?? (formData.currency as string) ?? "";
};


export function normalizeTemplateFormData(
  templateSteps: StepDefinition[],
  incomingFormData: unknown
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const incoming = (incomingFormData ?? {}) as Record<string, unknown>;

  for (const step of templateSteps) {
    const stepId: string =
      step.stepId ||
      String(step.title || "step")
        .toLowerCase()
        .replace(/\s+/g, "_");

    if (step.repeatable) {
      result[stepId] = Array.isArray(incoming[stepId]) ? incoming[stepId] : [];
      continue;
    }

    const stepIncoming = (incoming[stepId] ?? {}) as Record<string, unknown>;
    const stepResult: Record<string, unknown> = {};

    for (const fieldDef of step.fields ?? []) {
      if (Array.isArray(fieldDef.fields) && fieldDef.fields.length > 0) {
        const parentIncoming =
          typeof stepIncoming[fieldDef.key] === "object" &&
          stepIncoming[fieldDef.key] !== null
            ? (stepIncoming[fieldDef.key] as Record<string, unknown>)
            : {};

        const nestedResult: Record<string, unknown> = {};
        for (const nestedField of fieldDef.fields) {
          nestedResult[nestedField.key] =
            parentIncoming[nestedField.key] !== undefined
              ? parentIncoming[nestedField.key]
              : nestedField.default ??
                (nestedField.type === "checkbox" ? false : "");
        }
        stepResult[fieldDef.key] = nestedResult;
        continue;
      }

      const rawKey = String(fieldDef.key);
      const existingVal = stepIncoming[rawKey];

      if (existingVal !== undefined) {
        stepResult[rawKey] = existingVal;
      } else {
        if (fieldDef.type === "checkbox") {
          stepResult[rawKey] = fieldDef.default ?? false;
        } else if (fieldDef.type === "number") {
          stepResult[rawKey] = fieldDef.default ?? "";
        } else {
          stepResult[rawKey] = fieldDef.default ?? "";
        }
      }

      if (
        fieldDef.type === "fighter_search" ||
        rawKey.toLowerCase().endsWith("id")
      ) {
        const nameKey: string =
          fieldDef.nameField ||
          rawKey.replace(/id$/i, "Name");

        if (
          stepIncoming[nameKey] !== undefined &&
          stepResult[nameKey] === undefined
        ) {
          stepResult[nameKey] = stepIncoming[nameKey];
        }
      }
    }

    for (const [key, val] of Object.entries(stepIncoming)) {
      if (!(key in stepResult)) {
        stepResult[key] = val;
      }
    }

    result[stepId] = stepResult;
  }

  return result;
}

export interface DocumentTrackerItem {
  documentType: string;
  uploadStatus: string;
  deadlineDate?: string;
  uploadedFile?: string;
}

const DOCUMENT_TRACKER_KEY = "documentRequirement";

const UPLOADED_STATUSES = new Set(["UPLOADED", "APPROVED"]);
const UPLOADED_OR_UNDER_REVIEW_STATUSES = new Set([
  "UPLOADED",
  "PENDING_REVIEW",
  "APPROVED",
]);

const normalizeDocumentStatus = (status?: string): string =>
  String(status || "").trim().toUpperCase();

const hasDocumentFile = (item: DocumentTrackerItem): boolean =>
  typeof item.uploadedFile === "string" && item.uploadedFile.trim() !== "";

export const getDocumentTracker = (
  deal: DealResponseDto | undefined | null,
): { items: DocumentTrackerItem[] } | null => {
  const rawItems: unknown = deal?.formData?.[DOCUMENT_TRACKER_KEY];
  if (!Array.isArray(rawItems) || rawItems.length === 0) return null;

  const items: DocumentTrackerItem[] = rawItems
    .map((raw) => ({
      documentType: String(raw?.documentType ?? ""),
      uploadStatus: String(raw?.uploadStatus ?? "PENDING"),
      deadlineDate: raw?.deadlineDate,
      uploadedFile: raw?.uploadedFile,
    }))
    .filter((item) => item.documentType.trim() !== "");

  return items.length > 0 ? { items } : null;
};

export const getPendingRequiredDocuments = (
  deal: DealResponseDto | undefined | null,
): DocumentTrackerItem[] => {
  const tracker = getDocumentTracker(deal);
  if (!tracker) return [];

  return tracker.items.filter(
    (item) => !UPLOADED_STATUSES.has(normalizeDocumentStatus(item.uploadStatus)),
  );
};

export const getRequiredDocumentsWaitingForUpload = (
  deal: DealResponseDto | undefined | null,
): DocumentTrackerItem[] => {
  const tracker = getDocumentTracker(deal);
  if (!tracker) return [];

  return tracker.items.filter((item) => {
    const status = normalizeDocumentStatus(item.uploadStatus);

    if (status === "PENDING_REVIEW") {
      return !hasDocumentFile(item);
    }

    return !UPLOADED_OR_UNDER_REVIEW_STATUSES.has(status);
  });
};

export const hasRequiredDocumentsWaitingForUpload = (
  deal: DealResponseDto | undefined | null,
): boolean => getRequiredDocumentsWaitingForUpload(deal).length > 0;

export const getRequiredDocumentsPendingOrganizerApproval = (
  deal: DealResponseDto | undefined | null,
): DocumentTrackerItem[] => {
  const tracker = getDocumentTracker(deal);
  if (!tracker) return [];

  return tracker.items.filter(
    (item) =>
      normalizeDocumentStatus(item.uploadStatus) === "PENDING_REVIEW" &&
      hasDocumentFile(item),
  );
};

export const getOrganizerApprovalPendingMessage = (
  deal: DealResponseDto | undefined | null,
): string => {
  const documentTypes = getRequiredDocumentsPendingOrganizerApproval(deal)
    .map((item) => formatComponentLabelForUi(item.documentType))
    .filter(Boolean);

  if (documentTypes.length === 0) return "";

  return `Deal cannot be accepted. Documents pending organizer approval: ${documentTypes.join(", ")}`;
};